// src/services/vehicleService.js
// Service for fetching vehicles from the backend API using centralized HTTP client
import api from './http';
import i18n, { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../i18n/config';

// Vehicle feature names are stored per locale in the database, so the two
// endpoints that return features have to say which language they want. This is
// a plain module rather than a component, so the language comes from the i18n
// instance instead of a hook. An unknown value is not sent at all; the API
// falls back to English on its own.
function currentLang() {
  const lang = (i18n.resolvedLanguage || i18n.language || '').slice(0, 2);
  return SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
}

export async function fetchAvailableVehicles() {
  try {
    const response = await api.get(`/v1/vehicles?lang=${currentLang()}`);

    return Array.isArray(response.data.vehicles) ? response.data.vehicles : [];
  } catch (error) {
    console.error('Error fetching vehicles:', error);
    throw error;
  }
}

export async function fetchVehicleSeasonalRates(vehicleId) {
  try {
    const response = await api.get(`/v1/rates/${vehicleId}`);
    return response.data.vehicles || [];
  } catch (error) {
    console.error('Error fetching vehicle seasonal rates:', error);
    throw error;
  }
}

export async function fetchVehicleActiveRates(vehicleId) {
  try {
    const response = await api.get(`/v1/rates/active/${vehicleId}`);
    return response.data.vehicles || [];
  } catch (error) {
    console.error('Error fetching vehicle active rates:', error);
    throw error;
  }
}

export async function fetchSeasonalDates() {
  try {
    const response = await api.get('/v1/seasonal-dates');
    return response.data.seasonal_dates || [];
  } catch (error) {
    console.error('Error fetching seasonal dates:', error);
    throw error;
  }
}

export async function fetchVehicleDetails(vehicleId) {
  try {
    const response = await api.get(`/v1/vehicles/${vehicleId}?lang=${currentLang()}`);
    return response.data.vehicle || null;
  } catch (error) {
    console.error('Error fetching vehicle details:', error);
    throw error;
  }
}

export async function fetchAvailableVehiclesByDateRange(startDate, endDate) {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get(`/v1/vehicles/search?${params.toString()}`);
    return Array.isArray(response.data.vehicles) ? response.data.vehicles : [];
  } catch (error) {
    console.error('Error fetching vehicles by date range:', error);
    throw error;
  }
}

export async function fetchVehicleImages(vehicleId) {
  try {
    // Since there might not be a dedicated images endpoint, let's use the vehicles list
    // and extract the vehicle with all its images
    const vehicles = await fetchAvailableVehicles();
    const vehicle = vehicles.find(v => v.id === vehicleId.toString());

    if (!vehicle) {
      throw new Error('Vehicle not found');
    }

    // For now, we'll construct the image list from what we know
    // This should ideally be returned by the backend API
    return {
      mainImage: vehicle.image,
      thumbnailImages: [] // Backend needs to provide all images
    };
  } catch (error) {
    console.error('Error fetching vehicle images:', error);
    throw error;
  }
}

export async function fetchVehicleBlockedDates(vehicleId, startDate = null, endDate = null) {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const queryString = params.toString();
    const url = `/v1/blocked-dates/${vehicleId}${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data || { blocked_dates: [] };
  } catch (error) {
    console.error('Error fetching vehicle blocked dates:', error);
    return { blocked_dates: [] }; // Return empty array on error, don't throw
  }
}
